import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getUser, hasPageAccess, getDashboardPath, getRoleDisplayName } from '../../utils/auth';
import PoweredByFooter from '../brand/PoweredByFooter';

/**
 * RoleGuard component to protect routes based on user permissions
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if access is granted
 * @param {string[]} props.allowedRoles - Array of roles that can access this route
 * @param {string} props.redirectTo - Custom redirect path (optional)
 * @param {boolean} props.showAccessDenied - Show access denied message instead of redirect
 */
const RoleGuard = ({ 
  children, 
  allowedRoles = [], 
  redirectTo = null, 
  showAccessDenied = false 
}) => {
  const location = useLocation();
  const user = getUser();

  // Check if user is authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has access to current page
  const hasAccess = allowedRoles.length > 0 
    ? allowedRoles.includes(user.role)
    : hasPageAccess(location.pathname, user);

  if (!hasAccess) {
    if (showAccessDenied) {
      return (
        <div className="bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8" style={{ minHeight: "100dvh" }}>
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900">Access Denied</h3>
                <p className="mt-2 text-sm text-gray-600">
                  You don't have permission to access this page.
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Your role: <span className="font-medium">{getRoleDisplayName(user.role)}</span>
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Required roles: <span className="font-medium">{allowedRoles.map(role => getRoleDisplayName(role)).join(', ')}</span>
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => window.history.back()}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Go Back
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Redirect to appropriate dashboard or custom path
    const redirectPath = redirectTo || getDashboardPath(user);
    return <Navigate to={redirectPath} replace />;
  }

  // User has access, render the protected component
  return (
    <>
      {children}
      <PoweredByFooter />
    </>
  );
};

// Higher-order component for easy route protection
export const withRoleGuard = (Component, allowedRoles = [], options = {}) => {
  return (props) => (
    <RoleGuard allowedRoles={allowedRoles} {...options}>
      <Component {...props} />
    </RoleGuard>
  );
};

// Specific role guard components for common use cases
export const AdminGuard = ({ children, ...props }) => (
  <RoleGuard allowedRoles={['tenant_admin', 'superadmin']} {...props}>
    {children}
  </RoleGuard>
);

export const SuperAdminGuard = ({ children, ...props }) => (
  <RoleGuard allowedRoles={['superadmin']} {...props}>
    {children}
  </RoleGuard>
);

export const GovernanceGuard = ({ children, ...props }) => (
  <RoleGuard allowedRoles={['superadmin', 'governance_reader']} {...props}>
    {children}
  </RoleGuard>
);

export const PartnerGuard = ({ children, ...props }) => (
  <RoleGuard allowedRoles={['partner', 'tenant_admin', 'superadmin']} {...props}>
    {children}
  </RoleGuard>
);

export default RoleGuard;